import React, { useCallback, useRef, useState } from "react";
import Dropzone from "react-dropzone";
import Compress from "browser-image-compression";

import * as S from "./InputFile.styled";
import { ReactComponent as EyeShow } from "assets/svgs/eye-show.svg";
import { ReactComponent as RefreshIcon } from "assets/svgs/refresh.svg";
import { ReactComponent as MenuDots } from "assets/svgs/menu-dots.svg";
import { ReactComponent as Camera } from "assets/svgs/camera.svg";
import { ReactComponent as Trash } from "assets/svgs/trash.svg";
import { ReactComponent as ErrorIcon } from "assets/svgs/error.svg";
import { ReactComponent as IconError } from "assets/svgs/rounded-warning.svg";
import { generateBase64 } from "utils/inputUtils";

const getFileName = (error) => error ? "Tentar de novo" : "Mudar arquivo";

const InputFile = ({
  title,
  subTitle,
  details,
  file,
  setFile,
  maxSize,
  errorMessage,
  ...props
}) => {
  const hiddenFileInput = useRef(null);
  const hiddenCameraInput = useRef(null);
  const dropRef = useRef(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(1);
  const [shouldShowButtons, setShouldShowButtons] = useState(true);

  const compressImage = useCallback(
    (imgFile) => {
      const options = { maxSizeMB: 0.6, useWebWorker: false, onProgress };

      Compress(imgFile, options)
        .then((compressedBlob) => {
          compressedBlob.lastModifiedDate = new Date();
          const convertedBlobFile = new File([compressedBlob], imgFile.name, {
            type: imgFile.type,
            lastModified: Date.now(),
          });
          generateBase64(convertedBlobFile, setFile, setError);
          setLoading(false);
          setProgress(0);
        })
        .catch(() => {
          setError("Ops! Algo deu errado com o seu arquivo.");
          setLoading(false);
          setProgress(0);
        });
    },
    [setFile]
  );

  const onFileChange = useCallback(
    (e) => {
      setShouldShowButtons(true);
      const uploadedFile = e?.target?.files[0] || e[0];

      if (!uploadedFile) {
        return;
      }

      setLoading(true);

      if (uploadedFile?.type?.includes("image")) {
        compressImage(uploadedFile);
      } else if (uploadedFile?.size / 1000 < maxSize) {
        generateBase64(uploadedFile, setFile, setError);
        setError("");
        setLoading(false);
      } else if (uploadedFile?.size / 1000 > maxSize) {
        setError(`Tamanho máximo do arquivo é ${maxSize} KB`);
        setLoading(false);
      }
      e.target.value = ''
    },
    [compressImage, maxSize, setFile]
  );
  const handleChooseFile = (e) => {
    hiddenFileInput.current.click();
  };
  const handleTakePicture = (e) => {
    hiddenCameraInput.current.click();
  };
  const onProgress = (percentage) => {
    setProgress(percentage);
  };

  return (
    <div {...props}>
      <Dropzone
        onDrop={onFileChange}
        onDragOver={() => setShouldShowButtons(false)}
        onDragLeave={() => setShouldShowButtons(true)}
        accept={`image/jpg, image/jpeg, image/png, image/gif, image/bmp, 
        application/doc, application/docx, application/xls,
        application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
        application/vnd.ms-excel, application/pdf`}
      >
        {({ getRootProps, getInputProps }) => {
          const fileInputTitle = error ? error : title;
          return (
            <S.FileInputContainer
              {...getRootProps()}
              ref={dropRef} dragging={!shouldShowButtons} success={file} error={error} >
              <S.ContainerColumn>
                <S.TitleContainer>
                  {file?.name ? (
                    <S.FileInputTitleLink $shouldBeInFront={shouldShowButtons}>
                      <EyeShow /> {file.name}
                    </S.FileInputTitleLink>
                  ) : (
                    <S.FileInputTitle variant="headline-04" error={error}>{fileInputTitle}</S.FileInputTitle>
                  )}
                  {file && !loading && <S.StyledSuccessCircle />}
                  {!file && error && <ErrorIcon />}
                </S.TitleContainer>
                {file || error ? (
                  <>
                    <S.FileInputSubTitleLight>{title}</S.FileInputSubTitleLight>
                    <S.FileInputSubTitleLight>
                      {subTitle}
                    </S.FileInputSubTitleLight>
                  </>
                ) : (
                  <S.FileInputSubTitle>{subTitle}</S.FileInputSubTitle>
                )}
              </S.ContainerColumn>
              {!file && !loading && !error && (
                <S.ContainerRow
                  onClick={handleChooseFile}
                  $shouldBeInFront={shouldShowButtons} >
                  <S.StyledUploadDoc $shouldBeInFront={shouldShowButtons} />
                  <S.StyledLink>Clique ou solte o arquivo aqui</S.StyledLink>
                </S.ContainerRow>
              )}
              {loading && progress && <S.StyledProgressBar value={progress} />}
              {(file && !loading) || error ? (
                <S.ModifyButtonsContainer $shouldBeInFront={shouldShowButtons}>
                  <S.FlexRow
                    onClick={handleChooseFile}
                    style={{ cursor: "pointer" }}
                  >
                    <RefreshIcon />
                    <S.ButtonText>
                      {getFileName(error)}
                    </S.ButtonText>
                  </S.FlexRow>
                  <S.Bar />
                  {file && (
                    <S.FlexRow
                      onClick={() => {
                        setFile(null);
                        setError("");
                      }}
                      style={{ cursor: "pointer" }}>
                      <Trash /> <S.ButtonText>Excluir arquivo</S.ButtonText>
                    </S.FlexRow>
                  )}
                </S.ModifyButtonsContainer>
              ) : (
                !loading && (
                  <>
                    <S.StyledMobileButtons $shouldBeInFront={shouldShowButtons}>
                      <S.FlexRow
                        onClick={handleChooseFile} style={{ cursor: "pointer" }} >
                        <MenuDots /> <S.ButtonText>Escolher do celular</S.ButtonText>
                      </S.FlexRow>
                      <S.Bar />
                      <S.FlexRow
                        onClick={handleTakePicture}
                        style={{ cursor: "pointer" }}
                      >
                        <Camera /><S.ButtonText>Tirar foto</S.ButtonText>
                      </S.FlexRow>
                    </S.StyledMobileButtons>
                    <S.FileInputDetails>{details}</S.FileInputDetails>
                    <S.FileInputDetails> Tamanho máximo {maxSize} </S.FileInputDetails>
                  </>
                )
              )}

              <input
                {...getInputProps()}
                type="file"
                ref={hiddenFileInput}
                style={{ display: "none" }}
                onChange={onFileChange}
                accept="image/jpg, image/jpeg, image/png, image/gif, image/bmp, application/PDF, .DOC, .DOCX, .XLS, .XLSX."
              />
              <input
                ref={hiddenCameraInput}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onFileChange}
                style={{ display: "none" }}
              />
              {!file && errorMessage && <S.ErrorMessage><IconError />{errorMessage}</S.ErrorMessage>}
            </S.FileInputContainer>
          );
        }}
      </Dropzone>
    </div>
  );
};

export default InputFile;
